import { useState, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  Package2,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Phone,
  Calendar,
  Eye,
  X,
  AlertCircle,
  CheckCircle2,
  TruckIcon,
  UserCheck,
  PackageCheck,
  Navigation,
  RefreshCw,
  Key,
  BarChart3,
  Activity,
  Users,
  Package,
  RotateCcw,
  ArrowLeftRight,
} from "lucide-react";
import { ReturnsApi } from "../../services/returns-api";
import type { ReturnRequest } from "../../types/ReturnRequest";
import BaseApi from "../../services/base-api";
import Price from "../../components/Currency/Price";
import { useToast } from "../../context/ToastContext";
import ContactInfo from "../../components/ContactInfo";
import { useTranslation } from "react-i18next";
import LoadingComponent from "../../components/LoadingComponent";
import { WhatsAppService } from "../../services/whatsAppService";
import Pagination from "../../components/Pagination";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuth } from "../../context/AuthContext";

// Updated interfaces to match actual API response
export interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  whatsappNumber: string;
  email?: string;
  isAvailable?: boolean;
  currentDeliveries?: number;
  totalDeliveries?: number;
  rating?: number;
}

export interface Delivery {
  id: string;
  orderId: string;
  driverId: string | null;
  status: string;
  assignmentStatus?: string | null;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  deliveryOTP: string;
  otpVerified: boolean;
  notes: string | null;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  driver: Driver | null;
  order: any | null; // You'll need to populate this from the backend
  purpose?: "delivery" | "return_pickup" | "exchange_pickup" | null;
  returnRequestId?: string | null;
}

interface DeliveryStats {
  totalDeliveries: number;
  activeDeliveries: number;
  completedToday: number;
  avgDeliveryTime: number;
  byStatus: Record<string, number>;
}

interface DriverStats {
  driverId: string;
  driverName: string;
  totalDeliveries: number;
  completedDeliveries: number;
  pendingDeliveries: number;
  avgDeliveryTime: number;
  rating: number;
}

// "pickup_scheduled" -> "Pickup Scheduled" (matches the status labels used on the Returns page)
const humanizeStatus = (status: string) =>
  String(status || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const localizedName = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.en || value.ar || "";
};

const DeliveriesManagementPage = () => {
  const { t } = useTranslation();
  const { success, err } = useToast();
  const { user } = useAuth();
  const isDriverView = ["driver", "delivery_driver"].includes(
    String(user?.role || "").toLowerCase(),
  );

  // ========== ADD/UPDATE STATE VARIABLES ==========
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [filtered, setFiltered] = useState<Delivery[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim());
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [driverFilter, setDriverFilter] = useState<string>("all");
  const [zoneFilter, setZoneFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const latestRequestRef = useRef(0);
  const [apiTotalItems, setApiTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<"createdAt" | "status">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [deliveryStats, setDeliveryStats] = useState<DeliveryStats | null>(
    null,
  );
  const [driverStats, setDriverStats] = useState<DriverStats[]>([]);

  const [detailsModal, setDetailsModal] = useState(false);
  const [assignDriverModal, setAssignDriverModal] = useState(false);
  const [responseModal, setResponseModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [statsModal, setStatsModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(
    null,
  );
  const [selectedDriver, setSelectedDriver] = useState("");
  const [updating, setUpdating] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [responseDecision, setResponseDecision] = useState<
    "accept" | "reject"
  >("accept");
  const [responseReason, setResponseReason] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16),
  );
  const [paymentAmount, setPaymentAmount] = useState("0");
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Return/exchange pickup state
  const [linkedReturnRequest, setLinkedReturnRequest] = useState<ReturnRequest | null>(null);
  const [linkedReturnLoading, setLinkedReturnLoading] = useState(false);
  const [pickupRejectModal, setPickupRejectModal] = useState(false);
  const [pickupRejectionReason, setPickupRejectionReason] = useState("");
  const [completingPickup, setCompletingPickup] = useState(false);
  const [rejectingPickup, setRejectingPickup] = useState(false);
  const [pickupConditionChecked, setPickupConditionChecked] = useState(false);
  const linkedReturnRequestFor = useRef<string | null>(null);
  const [exchangeCompleteResult, setExchangeCompleteResult] = useState<{
    refundStatus?: string;
    priceDifference?: number | string | null;
    paymentURL?: string | null;
  } | null>(null);

  const deliveryStatuses = [
    {
      value: "pending",
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      icon: Clock,
    },
    {
      value: "confirmed",
      label: "Confirmed",
      color: "bg-blue-100 text-blue-800 border-blue-200",
      icon: CheckCircle,
    },
    {
      value: "assigned",
      label: "Assigned",
      color: "bg-blue-100 text-blue-800 border-blue-200",
      icon: UserCheck,
    },
    {
      value: "processing",
      label: "Processing",
      color: "bg-indigo-100 text-indigo-800 border-indigo-200",
      icon: Activity,
    },
    {
      value: "pickedUp",
      label: "Picked Up",
      color: "bg-purple-100 text-purple-800 border-purple-200",
      icon: PackageCheck,
    },
    {
      value: "inTransit",
      label: "In Transit",
      color: "bg-orange-100 text-orange-800 border-orange-200",
      icon: Navigation,
    },
    {
      value: "picked_up",
      label: "Picked Up",
      color: "bg-purple-100 text-purple-800 border-purple-200",
      icon: PackageCheck,
    },
    {
      value: "in_transit",
      label: "In Transit",
      color: "bg-orange-100 text-orange-800 border-orange-200",
      icon: Navigation,
    },
    {
      value: "delivered",
      label: "Delivered",
      color: "bg-green-100 text-green-800 border-green-200",
      icon: CheckCircle2,
    },
    {
      value: "completed",
      label: "Completed",
      color: "bg-green-100 text-green-800 border-green-200",
      icon: CheckCircle2,
    },
    {
      value: "rejected",
      label: "Rejected",
      color: "bg-red-100 text-red-800 border-red-200",
      icon: XCircle,
    },
    {
      value: "cancelled",
      label: "Cancelled",
      color: "bg-red-100 text-red-800 border-red-200",
      icon: XCircle,
    },
    {
      value: "failed",
      label: "Failed",
      color: "bg-red-100 text-red-800 border-red-200",
      icon: AlertCircle,
    },
  ];

  const deliveryFilterStatuses = [
    { value: "assigned", label: "Assigned" },
    { value: "picked_up", label: "Picked Up" },
    { value: "in_transit", label: "In Transit" },
    { value: "delivered", label: "Delivered" },
    { value: "failed", label: "Failed" },
  ];

  const getDeliveryZone = (delivery: Delivery) =>
    String(delivery.order?.shippingAddress?.zone || "").trim();

  const normalizeZone = (zone: string) => zone.trim().toLowerCase();

  const zoneOptions = Array.from(
    new Set(deliveries.map(getDeliveryZone).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));

  const driverFilterOptions: Array<
    Pick<Driver, "id" | "firstName" | "lastName">
  > =
    isDriverView && user?.id
      ? [
          {
            id: user.id,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
          },
        ]
      : drivers;

  useEffect(() => {
    if (isDriverView && user?.id && driverFilter !== user.id) {
      setDriverFilter(user.id);
    }
  }, [driverFilter, isDriverView, user?.id]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, driverFilter, zoneFilter]);

  useEffect(() => {
    if (zoneFilter === "all") {
      setFiltered(deliveries);
      return;
    }

    setFiltered(
      deliveries.filter(
        (delivery) =>
          normalizeZone(getDeliveryZone(delivery)) ===
          normalizeZone(zoneFilter),
      ),
    );
  }, [deliveries, zoneFilter]);

  useEffect(() => {
    fetchDeliveries();
    fetchAvailableDrivers();
    fetchDeliveryStats();
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    statusFilter,
    driverFilter,
    sortBy,
    sortOrder,
  ]);

  const fetchDeliveries = async () => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (driverFilter !== "all") {
        params.append("driverId", driverFilter);
      }

      const res = await BaseApi.get(`/delivery?${params.toString()}`);
      if (requestId !== latestRequestRef.current) return;
      if (res.success) {
        setDeliveries(res.data || []);
        setApiTotalItems(res.pagination?.totalItems || res.data.length);
      }
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error("Error fetching deliveries:", error);
      err(t("Failed to load deliveries"));
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const fetchInitialData = async () => {
    try {
      await Promise.all([
        fetchDeliveries(),
        fetchAvailableDrivers(),
        fetchDeliveryStats(),
      ]);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      err(t("Failed to load data"));
    }
  };

  const fetchActiveDeliveries = async () => {
    try {
      const res = await BaseApi.get(`/delivery/active`);
      if (res.success) {
        setDeliveries(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching active deliveries:", error);
      err(t("Failed to load active deliveries"));
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const res = await BaseApi.get(`/delivery/drivers/available`);
      if (res.success) {
        setDrivers(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  const fetchDeliveryStats = async () => {
    try {
      const res = await BaseApi.get(`/delivery/stats/overview`);
      if (res.success) {
        const data = res.data || {};
        const byStatus = Array.isArray(data.byStatus)
          ? data.byStatus.reduce(
              (
                acc: Record<string, number>,
                item: { status?: string; count?: string | number },
              ) => {
                const statusKey = String(item.status || "").toLowerCase();
                if (statusKey) {
                  acc[statusKey] = Number(item.count) || 0;
                }
                return acc;
              },
              {},
            )
          : {};

        setDeliveryStats({
          totalDeliveries: Number(data.total) || 0,
          activeDeliveries: Number(data.active) || 0,
          completedToday: Number(data.today?.completed) || 0,
          avgDeliveryTime: 0,
          byStatus: {
            assigned: Number(byStatus.assigned) || 0,
            picked_up: Number(byStatus.picked_up) || 0,
            in_transit: Number(byStatus.in_transit) || 0,
            delivered: Number(byStatus.delivered) || 0,
            failed: Number(byStatus.failed) || 0,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const syncDeliveryState = (deliveryId: string, updates: Partial<Delivery>) => {
    setDeliveries((prev) =>
      prev.map((delivery) =>
        delivery.id === deliveryId ? { ...delivery, ...updates } : delivery,
      ),
    );
    setFiltered((prev) =>
      prev.map((delivery) =>
        delivery.id === deliveryId ? { ...delivery, ...updates } : delivery,
      ),
    );
    setSelectedDelivery((prev) =>
      prev?.id === deliveryId ? { ...prev, ...updates } : prev,
    );
  };

  const notifyAssignedDriver = async (orderId: string, driverId: string) => {
    try {
      const res = await WhatsAppService.sendDeliveryAssignment(orderId);
      if (res.success) {
        success(t("Driver assigned and notified"));
      }
    } catch (error) {
      console.error("Failed to notify driver");
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDelivery || !selectedDriver) {
      err(t("Please select a driver"));
      return;
    }

    try {
      setUpdating(true);
      const res = await BaseApi.post(
        `/delivery/${selectedDelivery.id}/assign-driver`,
        {
          driverId: selectedDriver,
        },
      );

      if (res.success) {
        success(t("Driver assigned successfully"));
        await fetchDeliveries();
        await fetchAvailableDrivers();
        await notifyAssignedDriver(selectedDelivery.orderId, selectedDriver);
        setAssignDriverModal(false);
        setSelectedDriver("");
        setSelectedDelivery(null);
      }
    } catch (error) {
      console.error("Error assigning driver:", error);
      err(t("Failed to assign driver"));
    } finally {
      setUpdating(false);
    }
  };

  const handleSendOTP = async (delivery: Delivery) => {
    try {
      setSendingOTP(true);
      const res = await BaseApi.post(`/delivery/${delivery.id}/resend-otp`, {});

      if (res.success) {
        success(t("OTP sent to customer"));
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      err(t("Failed to send OTP"));
    } finally {
      setSendingOTP(false);
    }
  };

  const handleOpenResponseModal = (
    delivery: Delivery,
    decision: "accept" | "reject",
  ) => {
    setSelectedDelivery(delivery);
    setResponseDecision(decision);
    setResponseReason("");
    setResponseModal(true);
  };

  const handleRespondToDelivery = async () => {
    if (!selectedDelivery) return;
    if (responseDecision === "reject" && !responseReason.trim()) {
      err(t("Please provide a reason"));
      return;
    }

    try {
      setUpdating(true);
      const res = await BaseApi.post(`/delivery/${selectedDelivery.id}/respond`, {
        decision: responseDecision,
        reason:
          responseReason.trim() ||
          (responseDecision === "accept" ? "Approved" : "Rejected"),
      });

      if (res.success) {
        const updatedDelivery = res.data || {};
        syncDeliveryState(selectedDelivery.id, updatedDelivery);
        success(
          t(
            responseDecision === "accept"
              ? "Delivery approved successfully"
              : "Delivery rejected successfully",
          ),
        );
        setResponseModal(false);
        setResponseReason("");
        await fetchDeliveries();
        await fetchDeliveryStats();
        const linkedId =
          updatedDelivery.returnRequestId || selectedDelivery.returnRequestId;
        if (detailsModal && isReturnPickup(selectedDelivery) && linkedId) {
          await loadLinkedReturnRequest(linkedId);
        }
      } else {
        err(
          res?.message ||
            t(
              responseDecision === "accept"
                ? "Failed to approve delivery"
                : "Failed to reject delivery",
            ),
        );
      }
    } catch (error) {
      console.error("Error responding to delivery:", error);
      err(
        t(
          responseDecision === "accept"
            ? "Failed to approve delivery"
            : "Failed to reject delivery",
        ),
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenCompleteModal = (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setOtpCode("");
    setPaymentMode("");
    setPaymentDate(new Date().toISOString().slice(0, 16));
    setPaymentAmount(
      delivery.order?.total ? String(Number(delivery.order.total)) : "0",
    );
    setCompleteModal(true);
  };

  const handleVerifyOTP = async (deliveryId: string, otp: string) => {
    if (!paymentMode.trim()) {
      err(t("Please select payment mode"));
      return;
    }
    if (!paymentDate) {
      err(t("Please select payment date"));
      return;
    }

    try {
      setVerifyingOTP(true);
      const res = await BaseApi.post(`/delivery/${deliveryId}/verify-otp`, {
        otp,
        paymentMode: paymentMode.trim(),
        paymentDate: new Date(paymentDate).toISOString(),
        amount: Number(paymentAmount || 0),
      });

      if (res.success) {
        const updatedDelivery = res.data || {
          otpVerified: true,
          deliveredAt: new Date().toISOString(),
          status: "delivered",
        };
        syncDeliveryState(deliveryId, updatedDelivery);
        success(t("Delivery completed successfully"));
        setOtpCode("");
        setCompleteModal(false);
        await fetchDeliveries();
        await fetchDeliveryStats();
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      err(t("Invalid OTP"));
    } finally {
      setVerifyingOTP(false);
    }
  };

  const isReturnPickup = (delivery: Delivery) =>
    delivery.purpose === "return_pickup" || delivery.purpose === "exchange_pickup";

  // The :id for the pickup endpoints is the RETURN REQUEST id (delivery.returnRequestId),
  // not the delivery id. Its status gates the Complete/Reject pickup actions.
  const loadLinkedReturnRequest = async (returnRequestId: string) => {
    linkedReturnRequestFor.current = returnRequestId;
    setLinkedReturnLoading(true);
    try {
      const res = await ReturnsApi.getById(returnRequestId);
      if (linkedReturnRequestFor.current !== returnRequestId) return;
      if (res?.success && res.data) {
        setLinkedReturnRequest(res.data);
      } else {
        setLinkedReturnRequest(null);
        err(res?.message || t("Failed to load return request"));
      }
    } finally {
      if (linkedReturnRequestFor.current === returnRequestId) {
        setLinkedReturnLoading(false);
      }
    }
  };

  const handleViewDetails = async (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setDetailsModal(true);
    setLinkedReturnRequest(null);
    setLinkedReturnLoading(false);
    linkedReturnRequestFor.current = null;
    setExchangeCompleteResult(null);
    setPickupConditionChecked(false);

    if (!isReturnPickup(delivery)) return;

    let returnRequestId = delivery.returnRequestId;
    if (!returnRequestId) {
      // List payload may omit the link; fall back to the delivery detail.
      const res = await BaseApi.get(`/delivery/${delivery.id}`);
      if (res?.success && res.data) {
        returnRequestId = res.data.returnRequestId;
        if (returnRequestId) {
          syncDeliveryState(delivery.id, { returnRequestId });
        }
      }
    }

    if (returnRequestId) {
      await loadLinkedReturnRequest(returnRequestId);
    }
  };

  const canDoPickupActions = (delivery: Delivery) =>
    isReturnPickup(delivery) &&
    String(delivery.assignmentStatus || "").toLowerCase() === "accepted" &&
    linkedReturnRequest?.status === "pickup_scheduled";

  const handleCompletePickup = async () => {
    if (!selectedDelivery?.returnRequestId) return;
    try {
      setCompletingPickup(true);
      let res: any;
      if (selectedDelivery.purpose === "exchange_pickup") {
        res = await ReturnsApi.completeExchange(selectedDelivery.returnRequestId);
      } else {
        res = await ReturnsApi.completePickup(selectedDelivery.returnRequestId);
      }
      if (res?.success) {
        if (selectedDelivery.purpose === "exchange_pickup") {
          const result = res.data ?? res;
          setExchangeCompleteResult({
            refundStatus: result?.refundStatus,
            priceDifference: result?.priceDifference,
            paymentURL: result?.paymentURL ?? result?.paymentUrl ?? null,
          });
          success(t("Exchange completed"));
        } else {
          success(t("Pickup completed successfully"));
        }
        setPickupConditionChecked(false);
        await fetchDeliveries();
        await loadLinkedReturnRequest(selectedDelivery.returnRequestId);
      } else {
        err(res?.message || t("Failed to complete pickup"));
      }
    } catch (e: any) {
      err(e?.message || t("Failed to complete pickup"));
    } finally {
      setCompletingPickup(false);
    }
  };

  const handleRejectAtDoor = async () => {
    if (!selectedDelivery?.returnRequestId) return;
    if (!pickupRejectionReason.trim()) {
      err(t("Please provide a reason"));
      return;
    }
    try {
      setRejectingPickup(true);
      const res = await ReturnsApi.rejectPickup(
        selectedDelivery.returnRequestId,
        pickupRejectionReason.trim(),
      );
      if ((res as any)?.success) {
        success(t("Pickup rejected"));
        setPickupRejectModal(false);
        setPickupRejectionReason("");
        await fetchDeliveries();
        await loadLinkedReturnRequest(selectedDelivery.returnRequestId);
      } else {
        err((res as any)?.message || t("Failed to reject pickup"));
      }
    } catch (e: any) {
      err(e?.message || t("Failed to reject pickup"));
    } finally {
      setRejectingPickup(false);
    }
  };

  const handleOpenAssignDriver = (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setAssignDriverModal(true);
  };

  const canRespondToDelivery = (delivery: Delivery) =>
    String(delivery.assignmentStatus || "").toLowerCase() === "pending";

  const canCompleteDelivery = (delivery: Delivery) => {
    const status = String(delivery.status || "").toLowerCase();
    const assignmentStatus = String(
      delivery.assignmentStatus || "",
    ).toLowerCase();
    const hasAcceptedAssignment =
      !assignmentStatus ||
      ["accepted", "approved", "confirmed"].includes(assignmentStatus);

    return (
      !isReturnPickup(delivery) &&
      hasAcceptedAssignment &&
      !delivery.otpVerified &&
      !["pending", "cancelled", "rejected", "delivered", "completed"].includes(
        status,
      )
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusConfig = (status: string) => {
    return (
      deliveryStatuses.find((s) => s.value === status) || deliveryStatuses[0]
    );
  };

  if (initialLoading) {
    return <LoadingComponent title={t("Deliveries")} />;
  }

  const finalTotalItems =
    zoneFilter === "all" ? apiTotalItems : filtered.length;
  const finalTotalPages = Math.ceil(finalTotalItems / itemsPerPage);
  const currentPageData = filtered;

  return (
    <div className="min-h-screen bg-[#f2e6db] py-4 sm:py-8 px-4">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideRight { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div
          className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6"
          style={{ animation: "fadeIn 0.5s ease-out" }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-gray-600 to-black p-3 rounded-xl shadow-lg">
                <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  {t("Deliveries")}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {t("track-deliveries")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={() => setStatsModal(true)}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-sm flex items-center justify-center gap-2 shadow-lg"
              >
                <BarChart3 size={16} />
                <span className="hidden sm:inline">{t("Statistics")}</span>
                <span className="sm:hidden">{t("Stats")}</span>
              </button>
              <button
                onClick={fetchInitialData}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-sm flex items-center justify-center gap-2 shadow-lg"
              >
                <RefreshCw size={16} />
                <span className="hidden sm:inline">{t("Refresh")}</span>
              </button>
            </div>
          </div>

          {/* Statistics Overview */}
          {deliveryStats && (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6"
              style={{ animation: "fadeIn 0.4s ease-out" }}
            >
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md p-4 border-2 border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck className="w-5 h-5 text-blue-600" />
                  <p className="text-xs font-semibold text-blue-700">
                    {t("Assigned")}
                  </p>
                </div>
                <p className="text-2xl items-center font-bold text-blue-900">
                  {deliveryStats.byStatus?.assigned || 0}
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-md p-4 border-2 border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <PackageCheck className="w-5 h-5 text-purple-600" />
                  <p className="text-xs font-semibold text-purple-700">
                    {t("Picked Up")}
                  </p>
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {deliveryStats.byStatus?.picked_up || 0}
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-md p-4 border-2 border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <Navigation className="w-5 h-5 text-orange-600" />
                  <p className="text-xs font-semibold text-orange-700">
                    {t("In Transit")}
                  </p>
                </div>
                <p className="text-2xl font-bold text-orange-900">
                  {deliveryStats.byStatus?.in_transit || 0}
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md p-4 border-2 border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <p className="text-xs font-semibold text-green-700">
                    {t("Delivered")}
                  </p>
                </div>
                <p className="text-2xl font-bold text-green-900">
                  {deliveryStats.byStatus?.delivered || 0}
                </p>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-md p-4 border-2 border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-xs font-semibold text-red-700">
                    {t("Failed")}
                  </p>
                </div>
                <p className="text-2xl font-bold text-red-900">
                  {deliveryStats.byStatus?.failed || 0}
                </p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm sm:text-base bg-white"
                placeholder={t(
                  "Search by delivery ID, order ID, driver name...",
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-11 pr-3 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm sm:text-base appearance-none bg-white"
                >
                  <option value="all">{t("All Status")}</option>
                  {deliveryFilterStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {t(status.label)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <TruckIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={driverFilter}
                  onChange={(e) => setDriverFilter(e.target.value)}
                  disabled={isDriverView}
                  className="w-full pl-11 pr-3 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm sm:text-base appearance-none bg-white"
                >
                  {!isDriverView && <option value="all">{t("All Drivers")}</option>}
                  {driverFilterOptions.map((driver) => (
                    <option key={driver.id} value={driver.id}>
                      {driver.firstName} {driver.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={zoneFilter}
                  onChange={(e) => setZoneFilter(e.target.value)}
                  className="w-full pl-11 pr-3 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm sm:text-base appearance-none bg-white"
                >
                  <option value="all">{t("All Zones")}</option>
                  {zoneOptions.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={fetchActiveDeliveries}
                className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-semibold text-sm flex items-center justify-center gap-2"
              >
                <Navigation size={16} />
                {t("Active Only")}
              </button>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            {t("Showing")} {filtered.length} {t("of")} {deliveries.length}{" "}
            {t("deliveries")}
          </div>
        </div>

        {/* Deliveries List */}
        <div
          className={
            loading ? "opacity-50 pointer-events-none transition-opacity" : ""
          }
          style={{ animation: "fadeIn 0.5s ease-out 0.1s backwards" }}
        >
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-16 text-center">
              <Truck className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-base sm:text-lg">
                {t("No deliveries found")}
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {t("Try adjusting your filters")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentPageData.map((delivery: Delivery) => {
                const statusConfig = getStatusConfig(delivery.status);
                const StatusIcon = statusConfig.icon;

                return (
                  <div
                    key={delivery.id}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-4 border-2 border-gray-100 hover:border-indigo-200"
                    style={{
                      animation: "slideRight 0.3s ease-out",
                    }}
                  >
                    {/* Delivery Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b-2 border-gray-100">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="font-bold text-gray-900 text-lg">
                            #{delivery.id.slice(-8).toUpperCase()}
                          </div>
                          {delivery.orderId && (
                            <div className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                              Order: {delivery.orderId.slice(-8).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar size={14} />
                          {formatDate(delivery.createdAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 flex items-center gap-1 ${statusConfig.color}`}
                        >
                          <StatusIcon size={14} />
                          {t(statusConfig.label)}
                        </span>
                        {delivery.assignmentStatus && (
                          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                            {t("Assignment")}: {t(delivery.assignmentStatus)}
                          </span>
                        )}
                        {(!delivery.purpose || delivery.purpose === "delivery") && (
                          <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-100 text-gray-800 border-2 border-gray-200 flex items-center gap-1">
                            <Package size={12} />
                            {t("Delivery")}
                          </span>
                        )}
                        {delivery.purpose === "return_pickup" && (
                          <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-100 text-indigo-800 border-2 border-indigo-200 flex items-center gap-1">
                            <RotateCcw size={12} />
                            {t("Return Pickup")}
                          </span>
                        )}
                        {delivery.purpose === "exchange_pickup" && (
                          <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-teal-100 text-teal-800 border-2 border-teal-200 flex items-center gap-1">
                            <ArrowLeftRight size={12} />
                            {t("Exchange Pickup")}
                          </span>
                        )}
                        {delivery.driver && (
                          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-300 text-blue-700 border-2 border-blue-600 flex items-center gap-1">
                            <UserCheck size={14} />
                            {delivery.driver.firstName}
                          </span>
                        )}
                        {delivery.otpVerified && (
                          <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border-2 border-green-200 flex items-center gap-1">
                            <CheckCircle size={14} />
                            {t("Verified")}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Driver Info or Assign Button */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      {delivery.driver ? (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold text-gray-500 uppercase">
                            {t("Driver")}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <TruckIcon size={16} className="text-blue-600" />
                            <span className="font-medium text-blue-700">
                              {delivery.driver.firstName}{" "}
                              {delivery.driver.lastName}
                            </span>
                          </div>
                          {delivery.driver.phone && (
                            <ContactInfo
                              type="phone"
                              value={delivery.driver.phone}
                              icon={<Phone size={14} />}
                            />
                          )}
                        </div>
                      ) : !isDriverView ? (
                        <div className="flex items-center justify-center sm:justify-start">
                          <button
                            onClick={() => handleOpenAssignDriver(delivery)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold flex items-center gap-2"
                          >
                            <UserCheck size={16} />
                            {t("Assign Driver")}
                          </button>
                        </div>
                      ) : null}

                      {/* Delivery Times */}
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-gray-500 uppercase">
                          {t("Timeline")}
                        </div>
                        {delivery.pickedUpAt && (
                          <div className="text-sm text-gray-600">
                            <span className="font-semibold">
                              {t("Picked up")}:
                            </span>{" "}
                            {formatDate(delivery.pickedUpAt)}
                          </div>
                        )}
                        {delivery.deliveredAt && (
                          <div className="text-sm text-green-600">
                            <span className="font-semibold">
                              {t("Delivered")}:
                            </span>{" "}
                            {formatDate(delivery.deliveredAt)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    {delivery.notes && (
                      <div className="mb-4 bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertCircle size={16} className="text-yellow-600" />
                          <span className="text-sm font-semibold text-yellow-700">
                            {t("Notes")}
                          </span>
                        </div>
                        <p className="text-sm text-yellow-800">
                          {delivery.notes}
                        </p>
                      </div>
                    )}

                    {/* Failure Reason */}
                    {delivery.failureReason && (
                      <div className="mb-4 bg-red-50 rounded-lg p-3 border border-red-200">
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle size={16} className="text-red-600" />
                          <span className="text-sm font-semibold text-red-700">
                            {t("Failure Reason")}
                          </span>
                        </div>
                        <p className="text-sm text-red-800">
                          {delivery.failureReason}
                        </p>
                      </div>
                    )}

                    {/* Actions Bar */}
                    <div className="flex items-center justify-between pt-3 border-t-2 border-gray-100">
                      <div className="text-sm text-gray-500">
                        {!isDriverView &&
                          delivery.deliveryOTP &&
                          !delivery.otpVerified && (
                          <div className="flex items-center gap-2">
                            <Key size={14} />
                            <span>OTP: {delivery.deliveryOTP}</span>
                          </div>
                          )}
                      </div>
                      <div className="flex items-center gap-2">
                        {canRespondToDelivery(delivery) && (
                          <>
                            <button
                              onClick={() =>
                                handleOpenResponseModal(delivery, "accept")
                              }
                              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold flex items-center gap-1"
                            >
                              <CheckCircle size={14} />
                              {t("Approve")}
                            </button>
                            <button
                              onClick={() =>
                                handleOpenResponseModal(delivery, "reject")
                              }
                              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold flex items-center gap-1"
                            >
                              <XCircle size={14} />
                              {t("Reject")}
                            </button>
                          </>
                        )}
                        {canCompleteDelivery(delivery) &&
                          !delivery.otpVerified && (
                          <button
                            onClick={() => handleSendOTP(delivery)}
                            disabled={sendingOTP}
                            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-semibold flex items-center gap-1 disabled:opacity-50"
                          >
                            <Key size={14} />
                            {t("Resend OTP")}
                          </button>
                          )}
                        {isDriverView &&
                          isReturnPickup(delivery) &&
                          String(delivery.assignmentStatus || "").toLowerCase() ===
                            "accepted" && (
                            <button
                              onClick={() => handleViewDetails(delivery)}
                              className="px-3 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors text-sm font-semibold flex items-center gap-1"
                            >
                              <CheckCircle2 size={14} />
                              {t("Complete Pickup")}
                            </button>
                          )}
                        {canCompleteDelivery(delivery) && (
                          <button
                            onClick={() => handleOpenCompleteModal(delivery)}
                            className="px-3 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors text-sm font-semibold flex items-center gap-1"
                          >
                            <CheckCircle2 size={14} />
                            {t("Complete Delivery")}
                          </button>
                        )}
                        <button
                          onClick={() => handleViewDetails(delivery)}
                          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-semibold flex items-center gap-1"
                        >
                          <Eye size={14} />
                          {t("Details")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Pagination */}
              {finalTotalItems > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={finalTotalPages}
                  totalItems={finalTotalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(size) => {
                    setItemsPerPage(size);
                    setCurrentPage(1);
                  }}
                  pageSizeOptions={[5, 10, 25, 50, 100]}
                  showPageSizeSelector={true}
                  isLoading={loading}
                  compact={true}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delivery Details Modal */}
      {detailsModal && selectedDelivery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setDetailsModal(false);
            setSelectedDelivery(null);
          }}
          style={{ animation: "fadeIn 0.3s" }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div>
                <h3 className="text-xl font-bold text-white">
                  {t("Delivery Details")}
                </h3>
                <p className="text-sm text-blue-100">
                  #{selectedDelivery.id.slice(-12).toUpperCase()}
                </p>
              </div>
              <button
                onClick={() => {
                  setDetailsModal(false);
                  setSelectedDelivery(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {(canRespondToDelivery(selectedDelivery) ||
                canCompleteDelivery(selectedDelivery)) && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border-2 border-gray-200">
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    {t("Delivery Actions")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {canRespondToDelivery(selectedDelivery) && (
                      <>
                        <button
                          onClick={() =>
                            handleOpenResponseModal(selectedDelivery, "accept")
                          }
                          disabled={updating}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50"
                        >
                          {t("Approve Delivery")}
                        </button>
                        <button
                          onClick={() =>
                            handleOpenResponseModal(selectedDelivery, "reject")
                          }
                          disabled={updating}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50"
                        >
                          {t("Reject Delivery")}
                        </button>
                      </>
                    )}
                    {canCompleteDelivery(selectedDelivery) && (
                      <>
                        {!selectedDelivery.otpVerified && (
                          <button
                            onClick={() => handleSendOTP(selectedDelivery)}
                            disabled={sendingOTP}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold disabled:opacity-50"
                          >
                            {sendingOTP ? t("Sending...") : t("Resend OTP")}
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenCompleteModal(selectedDelivery)}
                          className="px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 transition-colors font-semibold"
                        >
                          {t("Complete Delivery")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Return / Exchange Pickup */}
              {isReturnPickup(selectedDelivery) && (
                <div className="bg-indigo-50 rounded-xl p-4 border-2 border-indigo-200 space-y-3">
                  <label className="block text-sm font-bold text-indigo-900 flex items-center gap-2">
                    {selectedDelivery.purpose === "return_pickup" ? (
                      <RotateCcw size={16} />
                    ) : (
                      <ArrowLeftRight size={16} />
                    )}
                    {selectedDelivery.purpose === "return_pickup"
                      ? t("Return Pickup")
                      : t("Exchange Pickup")}
                  </label>

                  {linkedReturnLoading ? (
                    <p className="text-sm text-indigo-700">{t("Loading...")}</p>
                  ) : !linkedReturnRequest && !exchangeCompleteResult ? (
                    <p className="text-sm text-indigo-700">
                      {t("Return request details are unavailable.")}
                    </p>
                  ) : (
                    <>
                      {linkedReturnRequest && (
                        <div className="text-sm text-indigo-900 space-y-2">
                          <div>
                            <span className="font-medium">{t("Request Status")}:</span>{" "}
                            <span className="font-semibold">
                              {t(humanizeStatus(linkedReturnRequest.status))}
                            </span>
                          </div>
                          {(linkedReturnRequest.items ?? []).length > 0 && (
                            <div>
                              <p className="font-medium mb-1">{t("Items to Collect")}:</p>
                              <ul className="list-disc ps-5 space-y-0.5">
                                {(linkedReturnRequest.items ?? []).map((item) => (
                                  <li key={item.id}>
                                    {localizedName(item.productName) ||
                                      localizedName(item.product?.name) ||
                                      localizedName((item.orderItem?.product as any)?.name) ||
                                      "-"}{" "}
                                    × {item.quantity}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {linkedReturnRequest.pickupRejectionReason && (
                            <div className="text-red-700">
                              <span className="font-medium">{t("Pickup Rejection")}:</span>{" "}
                              {linkedReturnRequest.pickupRejectionReason}
                            </div>
                          )}
                        </div>
                      )}

                      {exchangeCompleteResult ? (
                        <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm space-y-1">
                          {(() => {
                            const diff = Number(exchangeCompleteResult.priceDifference);
                            const hasDiff = Number.isFinite(diff) && diff !== 0;
                            const refundStatus = String(
                              exchangeCompleteResult.refundStatus || "",
                            ).toLowerCase();
                            const refunded =
                              !exchangeCompleteResult.paymentURL &&
                              ((!!refundStatus &&
                                !["none", "not_applicable", "not_required"].includes(
                                  refundStatus,
                                )) ||
                                (hasDiff && diff < 0));
                            return (
                              <>
                                <p className="font-semibold text-green-800">
                                  {t("Exchange completed")}
                                </p>
                                <p className="text-green-800">
                                  {exchangeCompleteResult.paymentURL
                                    ? t("Payment link sent to customer for the price difference")
                                    : refunded
                                    ? t("Price difference refunded to customer")
                                    : t("No additional charge")}
                                  {hasDiff && (
                                    <>
                                      {" "}
                                      (<Price amount={Math.abs(diff)} />)
                                    </>
                                  )}
                                </p>
                                {refunded && exchangeCompleteResult.refundStatus && (
                                  <p className="text-green-700">
                                    {t("Refund Status")}:{" "}
                                    {t(humanizeStatus(exchangeCompleteResult.refundStatus))}
                                  </p>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      ) : isDriverView && canDoPickupActions(selectedDelivery) ? (
                        <div className="space-y-3">
                          <label className="flex items-start gap-2 text-sm text-indigo-900 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={pickupConditionChecked}
                              onChange={(e) => setPickupConditionChecked(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span>
                              {t("I have inspected the item(s) and the condition is acceptable")}
                            </span>
                          </label>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={handleCompletePickup}
                              disabled={completingPickup || rejectingPickup || !pickupConditionChecked}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm disabled:opacity-50 flex items-center gap-1"
                            >
                              {completingPickup ? t("Completing...") : t("Complete Pickup")}
                            </button>
                            <button
                              onClick={() => {
                                setPickupRejectionReason("");
                                setPickupRejectModal(true);
                              }}
                              disabled={completingPickup || rejectingPickup}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-sm disabled:opacity-50"
                            >
                              {t("Reject at Door")}
                            </button>
                          </div>
                        </div>
                      ) : isDriverView &&
                        linkedReturnRequest &&
                        ["driver_assigned", "pickup_scheduled"].includes(
                          linkedReturnRequest.status,
                        ) ? (
                        <p className="text-sm text-indigo-700">
                          {String(selectedDelivery.assignmentStatus || "").toLowerCase() !==
                          "accepted"
                            ? t("Accept the assignment first to enable pickup actions.")
                            : t("Pickup actions are available once the request status is pickup_scheduled.")}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              )}

            {canCompleteDelivery(selectedDelivery) &&
                selectedDelivery.deliveryOTP && (
                  <div className="bg-purple-50 rounded-xl p-4 border-2 border-purple-200">
                    <h4 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                      <Key size={18} />
                      {t("OTP Verification")}
                    </h4>
                    {!isDriverView && (
                      <div className="text-sm text-purple-800">
                        <strong>{t("Current OTP")}:</strong>{" "}
                        {selectedDelivery.deliveryOTP}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-purple-700">
                      {isDriverView
                        ? t(
                            "Ask the customer for the OTP, then use Complete Delivery to verify it and finish the delivery.",
                          )
                        : t(
                            "Use Complete Delivery to verify OTP, capture payment details, and finish the delivery.",
                          )}
                    </p>
                  </div>
                )}

              {/* Driver Information */}
              <div className="border-2 border-blue-300 rounded-xl p-4 bg-blue-50">
                <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <TruckIcon size={18} />
                  {t("Driver Information")}
                </h4>
                {selectedDelivery.driver ? (
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">
                        {t("Name")}:
                      </span>
                      <span className="ml-2 font-semibold text-slate-900">
                        {selectedDelivery.driver.firstName}{" "}
                        {selectedDelivery.driver.lastName}
                      </span>
                    </div>
                    {selectedDelivery.driver.phone && (
                      <div>
                        <span className="text-blue-700 font-medium">
                          {t("Phone")}:
                        </span>
                        <a
                          href={`tel:${selectedDelivery.driver.phone}`}
                          className="ml-2 font-medium text-slate-900 hover:text-blue-700 transition-colors"
                        >
                          {selectedDelivery.driver.phone}
                        </a>
                      </div>
                    )}
                    {selectedDelivery.driver.whatsappNumber && (
                      <div>
                        <span className="text-blue-700 font-medium">
                          {t("WhatsApp")}:
                        </span>
                        <a
                          href={`https://wa.me/${selectedDelivery.driver.whatsappNumber.replace(/\D/g, "")}`}
                          className="ml-2 font-medium text-slate-900 hover:text-blue-700 transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {selectedDelivery.driver.whatsappNumber}
                        </a>
                      </div>
                    )}
                  </div>
                ) : !isDriverView ? (
                  <div className="text-center py-4">
                    <p className="text-blue-700 mb-3">
                      {t("No driver assigned")}
                    </p>
                    <button
                      onClick={() => {
                        setDetailsModal(false);
                        handleOpenAssignDriver(selectedDelivery);
                      }}
                      className="px-4 py-2 bg-[#0d223f] text-white rounded-lg hover:bg-[#0d223f] transition-colors font-semibold text-sm"
                    >
                      {t("Assign Driver")}
                    </button>
                  </div>
                ) : null}
              </div>

              {/* Timeline */}
              <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock size={18} />
                  {t("Timeline")}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("Created")}:</span>
                    <span className="font-semibold">
                      {formatDate(selectedDelivery.createdAt)}
                    </span>
                  </div>
                  {selectedDelivery.pickedUpAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("Picked Up")}:</span>
                      <span className="font-semibold text-purple-700">
                        {formatDate(selectedDelivery.pickedUpAt)}
                      </span>
                    </div>
                  )}
                  {selectedDelivery.deliveredAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t("Delivered")}:</span>
                      <span className="font-semibold text-green-700">
                        {formatDate(selectedDelivery.deliveredAt)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t("Last Updated")}:</span>
                    <span className="font-semibold">
                      {formatDate(selectedDelivery.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedDelivery.notes && (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
                  <h4 className="font-bold text-yellow-900 mb-2 flex items-center gap-2">
                    <AlertCircle size={18} />
                    {t("Notes")}
                  </h4>
                  <p className="text-sm text-yellow-800">
                    {selectedDelivery.notes}
                  </p>
                </div>
              )}

              {/* Failure Reason */}
              {selectedDelivery.failureReason && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                  <h4 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                    <XCircle size={18} />
                    {t("Failure Reason")}
                  </h4>
                  <p className="text-sm text-red-800">
                    {selectedDelivery.failureReason}
                  </p>
                </div>
              )}

              {/* Order Information Note */}
              <div className="bg-blue-100 border-2 border-blue-500 rounded-xl p-4">
                <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <Package2 size={18} />
                  {t("Order Information")}
                </h4>
                <p className="text-sm text-blue-800">
                  {t("Order ID")}: {selectedDelivery.orderId}
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  {t(
                    "Note: Full order details need to be fetched from the order endpoint",
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {responseModal && selectedDelivery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setResponseModal(false);
            setResponseReason("");
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {responseDecision === "accept"
                  ? t("Approve Delivery")
                  : t("Reject Delivery")}
              </h3>
              <button
                onClick={() => {
                  setResponseModal(false);
                  setResponseReason("");
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600">
                {t("Delivery ID")}:{" "}
                <span className="font-semibold text-gray-900">
                  {selectedDelivery.id.slice(-12).toUpperCase()}
                </span>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Reason")}
                  {responseDecision === "reject" && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>
                <textarea
                  value={responseReason}
                  onChange={(e) => setResponseReason(e.target.value)}
                  rows={4}
                  placeholder={
                    responseDecision === "accept"
                      ? t("Approved")
                      : t("On Leave")
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setResponseModal(false);
                    setResponseReason("");
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleRespondToDelivery}
                  disabled={updating}
                  className={`flex-1 px-6 py-3 text-white rounded-lg font-semibold disabled:opacity-50 ${
                    responseDecision === "accept"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {updating
                    ? t("Submitting...")
                    : responseDecision === "accept"
                      ? t("Approve")
                      : t("Reject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {completeModal && selectedDelivery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setCompleteModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {t("Complete Delivery")}
              </h3>
              <button
                onClick={() => setCompleteModal(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-900">
                <div>
                  <strong>{t("Delivery ID")}:</strong>{" "}
                  {selectedDelivery.id.slice(-12).toUpperCase()}
                </div>
                {!isDriverView && selectedDelivery.deliveryOTP && (
                  <div className="mt-1">
                    <strong>{t("Current OTP")}:</strong>{" "}
                    {selectedDelivery.deliveryOTP}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("OTP")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder={t("Enter OTP")}
                  maxLength={6}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Payment Mode")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  placeholder={t("cash / card / online")}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Payment Date")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Amount")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                {!isDriverView && (
                  <button
                    onClick={() => handleSendOTP(selectedDelivery)}
                    disabled={sendingOTP}
                    className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50"
                  >
                    {sendingOTP ? t("Sending...") : t("Resend OTP")}
                  </button>
                )}
                <button
                  onClick={() => handleVerifyOTP(selectedDelivery.id, otpCode)}
                  disabled={verifyingOTP || !otpCode || !paymentMode}
                  className="flex-1 px-6 py-3 bg-green-700 text-white rounded-lg hover:bg-green-800 font-semibold disabled:opacity-50"
                >
                  {verifyingOTP ? t("Verifying...") : t("Verify & Complete")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {assignDriverModal && selectedDelivery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setAssignDriverModal(false);
            setSelectedDelivery(null);
            setSelectedDriver("");
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <UserCheck size={24} />
                {t("Assign Driver")}
              </h3>
              <button
                onClick={() => {
                  setAssignDriverModal(false);
                  setSelectedDelivery(null);
                  setSelectedDriver("");
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4 p-3 bg-blue-200 rounded-lg border border-blue-600">
                <p className="text-sm text-blue-900">
                  <strong>{t("Delivery ID")}:</strong>{" "}
                  {selectedDelivery.id.slice(-12).toUpperCase()}
                </p>
                <p className="text-sm text-blue-900">
                  <strong>{t("Order ID")}:</strong>{" "}
                  {selectedDelivery.orderId.slice(-12).toUpperCase()}
                </p>
              </div>

              <label className="block text-sm font-bold text-gray-700 mb-3">
                {t("Select Available Driver")}
              </label>

              {drivers.length === 0 ? (
                <div className="text-center py-8">
                  <TruckIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">{t("No available drivers")}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {drivers.map((driver) => (
                    <button
                      key={driver.id}
                      onClick={() => setSelectedDriver(driver.id)}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                        selectedDriver === driver.id
                          ? "border-blue-300 bg-blue-200"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                            {driver.firstName[0]}
                            {driver.lastName[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">
                              {driver.firstName} {driver.lastName}
                            </div>
                            <div className="text-sm text-gray-600">
                              {driver.phone}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {driver.totalDeliveries !== undefined && (
                                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-semibold">
                                  {driver.totalDeliveries} {t("deliveries")}
                                </span>
                              )}
                              {driver.isAvailable && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-semibold">
                                  {t("Available")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {selectedDriver === driver.id && (
                          <CheckCircle className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setAssignDriverModal(false);
                    setSelectedDelivery(null);
                    setSelectedDriver("");
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleAssignDriver}
                  disabled={updating || !selectedDriver}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 transition-colors"
                >
                  {updating ? t("Assigning...") : t("Assign Driver")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {statsModal && deliveryStats && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setStatsModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <BarChart3 size={24} />
                {t("Delivery Statistics")}
              </h3>
              <button
                onClick={() => setStatsModal(false)}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Overview Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Package size={20} className="text-blue-600" />
                    <p className="text-sm font-semibold text-blue-700">
                      {t("Total Deliveries")}
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-blue-900">
                    {deliveryStats.totalDeliveries}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Navigation size={20} className="text-orange-600" />
                    <p className="text-sm font-semibold text-orange-700">
                      {t("Active Deliveries")}
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-orange-900">
                    {deliveryStats.activeDeliveries}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={20} className="text-green-600" />
                    <p className="text-sm font-semibold text-green-700">
                      {t("Completed Today")}
                    </p>
                  </div>
                  <p className="text-3xl font-bold text-green-900">
                    {deliveryStats.completedToday}
                  </p>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity size={20} />
                  {t("Status Breakdown")}
                </h4>
                <div className="space-y-3">
                  {Object.entries(deliveryStats.byStatus || {}).map(
                    ([status, count]) => {
                      const statusConfig = getStatusConfig(status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <div
                          key={status}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`p-2 rounded-lg ${statusConfig.color}`}
                            >
                              <StatusIcon size={18} />
                            </div>
                            <span className="font-semibold text-gray-900 capitalize">
                              {t(statusConfig.label)}
                            </span>
                          </div>
                          <span className="text-xl font-bold text-gray-900">
                            {count}
                          </span>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>

              {/* Driver Stats */}
              {driverStats.length > 0 && (
                <div className="border-2 border-gray-200 rounded-xl p-4 bg-white">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Users size={20} />
                    {t("Driver Performance")}
                  </h4>
                  <div className="space-y-3">
                    {driverStats.map((stat) => (
                      <div
                        key={stat.driverId}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <div className="font-semibold text-gray-900">
                            {stat.driverName}
                          </div>
                          <div className="text-sm text-gray-600">
                            {stat.completedDeliveries} {t("completed")} •{" "}
                            {stat.pendingDeliveries} {t("pending")}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">
                            {t("Total")}
                          </div>
                          <div className="text-xl font-bold text-gray-900">
                            {stat.totalDeliveries}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject at Door Modal */}
      {pickupRejectModal && selectedDelivery && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
          onClick={() => {
            setPickupRejectModal(false);
            setPickupRejectionReason("");
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "slideDown 0.3s" }}
          >
            <div className="bg-gradient-to-r from-red-700 to-red-900 px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">{t("Reject at Door")}</h3>
              <button
                onClick={() => {
                  setPickupRejectModal(false);
                  setPickupRejectionReason("");
                }}
                className="text-white hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Rejection Reason")} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={pickupRejectionReason}
                  onChange={(e) => setPickupRejectionReason(e.target.value)}
                  rows={4}
                  placeholder={t("Describe the condition issue or reason for rejection")}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setPickupRejectModal(false);
                    setPickupRejectionReason("");
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleRejectAtDoor}
                  disabled={rejectingPickup || !pickupRejectionReason.trim()}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50"
                >
                  {rejectingPickup ? t("Submitting...") : t("Confirm Rejection")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveriesManagementPage;
